import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layers, UploadCloud, Sparkles, X, Eye, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ACCEPTED = ".jpg,.jpeg,.png,.tif,.tiff,.fits,.fit,.fts,.xisf";

export default function Batch() {
  const [modes, setModes] = useState([]);
  const [mode, setMode] = useState("naturel");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [batch, setBatch] = useState(null);
  const inputRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => { api.get("/modes").then((r) => setModes(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!batchId) return;
    const tick = async () => {
      try {
        const { data } = await api.get(`/batches/${batchId}`);
        setBatch(data);
        if (data.status === "complete") {
          clearInterval(pollRef.current);
          toast.success(`Lot terminé : ${data.completed} succès, ${data.failed} échec(s)`);
        }
      } catch (e) {
        clearInterval(pollRef.current);
      }
    };
    tick();
    pollRef.current = setInterval(tick, 1500);
    return () => clearInterval(pollRef.current);
  }, [batchId]);

  const onPick = (list) => {
    if (!list) return;
    const arr = Array.from(list).slice(0, 20);
    const total = arr.reduce((s, f) => s + f.size, 0);
    if (total > 500 * 1024 * 1024) { toast.error("Lot trop volumineux (max 500 Mo cumulés)"); return; }
    setFiles(arr);
  };
  const removeAt = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const onSubmit = async () => {
    if (files.length === 0) { toast.error("Aucun fichier sélectionné"); return; }
    setBusy(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    fd.append("mode", mode);
    try {
      const { data } = await api.post("/batches", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBatchId(data.id);
      toast.success(`Lot lancé : ${data.total} images`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec du lancement");
      setBusy(false);
    }
  };

  const reset = () => { setFiles([]); setBatch(null); setBatchId(null); setBusy(false); };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow inline-flex items-center gap-2"><Layers className="w-3 h-3" /> Lot</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-light tracking-tighter">Traitement par lot</h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">Jusqu'à 20 astrophotos traitées en série, avec le même mode. Vous pouvez fermer cette page : le lot continuera en arrière-plan.</p>
        </div>
        <Link to="/" className="text-sm text-muted-foreground hover:text-white">↩ Mode image unique</Link>
      </div>

      {!batchId ? (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-8 rounded-md border border-white/10 bg-card overflow-hidden">
            <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
              <div className="label-eyebrow">Étape 1 · Sélectionner les images</div>
              <div className="text-[11px] text-muted-foreground">jusqu'à 20 fichiers · 500 Mo cumulés</div>
            </div>
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); onPick(e.dataTransfer.files); }}
              onClick={() => inputRef.current?.click()}
              className="relative min-h-[280px] grid place-items-center cursor-pointer transition-colors hover:bg-white/[0.02] p-6"
              data-testid="batch-dropzone"
            >
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPTED}
                multiple
                className="hidden"
                onChange={(e) => onPick(e.target.files)}
                data-testid="batch-file-input"
              />
              {files.length === 0 ? (
                <div className="text-center">
                  <div className="w-14 h-14 mx-auto rounded-full border border-white/10 grid place-items-center mb-4">
                    <UploadCloud className="w-6 h-6 text-violet-300" strokeWidth={1.2} />
                  </div>
                  <div className="font-light text-lg tracking-tight">Glissez plusieurs astrophotos ici</div>
                  <div className="text-xs text-muted-foreground mt-1">ou cliquez pour sélectionner plusieurs fichiers</div>
                </div>
              ) : (
                <div className="w-full">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {files.map((f, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-white/10 bg-secondary/30">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-mono truncate">{f.name}</div>
                          <div className="text-[10px] text-muted-foreground">{(f.size / 1024 / 1024).toFixed(2)} Mo</div>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); removeAt(i); }} className="p-1 rounded hover:bg-white/10" data-testid={`batch-remove-${i}`}>
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 text-[11px] text-muted-foreground">{files.length} fichier{files.length > 1 ? "s" : ""} · {(files.reduce((s, f) => s + f.size, 0) / 1024 / 1024).toFixed(2)} Mo cumulés</div>
                </div>
              )}
            </div>
          </div>

          <div className="md:col-span-4 rounded-md border border-white/10 bg-card flex flex-col">
            <div className="px-5 py-3 border-b border-white/5">
              <div className="label-eyebrow">Étape 2 · Mode unique appliqué à tout le lot</div>
            </div>
            <div className="p-3 space-y-2 flex-1 overflow-auto">
              {modes.map((m) => {
                const active = mode === m.key;
                return (
                  <button key={m.key} onClick={() => setMode(m.key)} data-testid={`batch-mode-${m.key}`}
                    className={`w-full text-left rounded-md border p-3 transition-all ${active ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/20"}`}>
                    <div className="font-medium tracking-tight text-sm">{m.label}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">{m.summary}</div>
                  </button>
                );
              })}
            </div>
            <div className="p-4 border-t border-white/5">
              <button onClick={onSubmit} disabled={busy || files.length === 0} data-testid="batch-start-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-md py-3 text-sm font-medium hover:bg-primary/90 transition-all glow-violet disabled:opacity-60 disabled:cursor-not-allowed">
                <Sparkles className="w-4 h-4" /> Lancer le traitement
              </button>
            </div>
          </div>
        </div>
      ) : (
        <BatchProgress batch={batch} onReset={reset} />
      )}
    </div>
  );
}


function BatchProgress({ batch, onReset }) {
  const nav = useNavigate();
  if (!batch) return <div className="text-muted-foreground">Initialisation du lot…</div>;
  const pct = batch.total ? Math.round((batch.completed + batch.failed) * 100 / batch.total) : 0;
  return (
    <div className="space-y-6" data-testid="batch-progress">
      <div className="rounded-md border border-white/10 bg-card p-5">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="label-eyebrow">Mode : {batch.mode_label}</div>
            <h2 className="mt-1 text-2xl font-light tracking-tight">Traitement en cours…</h2>
          </div>
          <div className="font-mono text-sm text-muted-foreground">
            {batch.completed + batch.failed} / {batch.total} · {pct}%
          </div>
        </div>
        <div className="mt-4 h-2 rounded-full bg-secondary overflow-hidden">
          <div className="h-full bg-gradient-to-r from-violet-500 to-blue-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
        {batch.status === "complete" && (
          <div className="mt-4 flex flex-wrap gap-3">
            <button onClick={() => nav("/library")} className="px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90 glow-violet" data-testid="batch-to-library-btn">Voir dans la médiathèque</button>
            <button onClick={onReset} className="px-3 py-2 rounded-md border border-white/10 text-sm hover:bg-white/5" data-testid="batch-new-btn">Nouveau lot</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="batch-items">
        {batch.items.map((it, i) => (
          <div key={i} className="rounded-md border border-white/10 bg-card p-3 flex items-center gap-3">
            <div className="w-7 h-7 grid place-items-center flex-none">
              {it.status === "done" && <CheckCircle2 className="w-5 h-5 text-emerald-400" />}
              {it.status === "error" && <XCircle className="w-5 h-5 text-red-400" />}
              {it.status === "pending" && <Loader2 className="w-5 h-5 text-violet-300 animate-spin" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm truncate font-mono">{it.filename}</div>
              <div className="text-[11px] text-muted-foreground truncate">
                {it.status === "pending" && "En attente / traitement…"}
                {it.status === "done" && "Traitée"}
                {it.status === "error" && (it.error || "Erreur")}
              </div>
            </div>
            {it.image_id && (
              <Link to={`/image/${it.image_id}`} className="p-2 rounded hover:bg-white/5" data-testid={`batch-view-${i}`}>
                <Eye className="w-4 h-4" />
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
